import * as React from 'react';

import { Button } from './Button';
import { cn } from './cn';
import { IconButton } from './IconButton';
import { Text } from './Typography';

// FileInput — labelled file picker primitive. Wraps the native
// `<input type="file">` (hidden) behind a visible Button trigger plus a
// filename slot and a clear affordance. Bottom-of-stack like Input — the
// chrome IS the primitive, so geometry lives inline (same `wm-field`
// shape as Input/Select/TextArea).
//
// Value is the selected `File` (or `null`). `onChange(file)` fires with
// the File or `null` — no synthetic event wrapping. RHF Controllers
// hand `field.onChange` straight through.

const WM_FIELD_BASE_CLASSNAME = 'wm-field [--wm-field-border-width:1px] [--wm-field-margin:0px] [--wm-field-padding-x:0.5rem] [--wm-field-padding-y:0.25rem] tw-m-0 tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-input tw-bg-background tw-ring-offset-background tw-transition-colors disabled:tw-cursor-not-allowed disabled:tw-opacity-45';

// Canonical control-height scale (small=32, medium=40, large=44) — same
// as Input/Button so a FileInput sits inline with peers.
const WM_FIELD_SIZE_CLASSNAMES = {
    small: 'tw-h-8 tw-px-1 tw-py-0.5 tw-text-xs',
    medium: 'tw-h-10 tw-px-1.5 tw-py-1 tw-text-sm',
    large: 'tw-h-11 tw-px-2 tw-py-1.5 tw-text-sm'
};

const BUTTON_SIZE_BY_FIELD_SIZE = {
    small: 'small',
    medium: 'small',
    large: 'medium'
};

const FileInput = React.forwardRef((
    {
        accept,
        buttonLabel,
        clearable = true,
        disabled,
        id,
        name,
        onBlur,
        onChange,
        placeholder,
        size = 'medium',
        value
    },
    ref
) => {
    const resolvedSize = WM_FIELD_SIZE_CLASSNAMES[size] ? size : 'medium';
    const inputRef = React.useRef(null);

    React.useImperativeHandle(ref, () => inputRef.current);

    // The native input is purely a picker — value is owned by the
    // parent (controlled). When the parent clears value back to null we
    // wipe the native input too, otherwise re-selecting the same file
    // would not refire `change`.
    React.useEffect(() => {
        if (!value && inputRef.current) {
            inputRef.current.value = '';
        }
    }, [value]);

    const handleNativeChange = (event) => {
        const file = event.target.files?.[0] ?? null;
        onChange?.(file);
    };

    const handlePick = () => {
        if (disabled) {
            return;
        }
        inputRef.current?.click();
    };

    // Clear is structured the same way Input's clear is: stop the click
    // from bubbling into anything that hosts the field (a Label with
    // htmlFor=id, for instance, would re-open the file picker on bubble),
    // wipe the native input synchronously so a re-pick of the same file
    // still fires `change`, then push `null` upstream.
    const handleClear = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (disabled) {
            return;
        }
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        onChange?.(null);
    };

    const resolvedButtonLabel = buttonLabel ?? i18next.t('ui.buttons.choose_file', { defaultValue: 'Choose file' });
    const resolvedPlaceholder = placeholder ?? i18next.t('ui.no_file_selected', { defaultValue: 'No file selected' });

    return (
        <div
            className={cn(
                WM_FIELD_BASE_CLASSNAME,
                WM_FIELD_SIZE_CLASSNAMES[resolvedSize]
            )}
        >
            <input
                accept={accept}
                className="tw-sr-only"
                disabled={disabled}
                id={id}
                name={name}
                onBlur={onBlur}
                onChange={handleNativeChange}
                ref={inputRef}
                tabIndex={-1}
                type="file"
            />
            <Button
                appearance="outline"
                disabled={disabled}
                icon="upload"
                onClick={handlePick}
                size={BUTTON_SIZE_BY_FIELD_SIZE[resolvedSize]}
                tone="neutral"
                type="button"
            >
                {resolvedButtonLabel}
            </Button>
            <span className="tw-min-w-0 tw-flex-1 tw-truncate">
                {value ? (
                    <Text variant="body">{value.name}</Text>
                ) : (
                    <Text tone="muted" variant="body">{resolvedPlaceholder}</Text>
                )}
            </span>
            {clearable && value ? (
                <IconButton
                    aria-label={i18next.t('common.clear')}
                    disabled={disabled}
                    icon="close"
                    onClick={handleClear}
                    // Without this, mousedown moves focus away from the
                    // hidden file input and a parent <label htmlFor=id>
                    // can re-open the picker on the click that follows.
                    // Same pattern Input uses for its inline clear.
                    onMouseDown={event => event.preventDefault()}
                    size="tiny"
                />
            ) : null}
        </div>
    );
});
FileInput.displayName = 'FileInput';

export { FileInput };
